import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../request/http-common';
import {
  Box, Typography, FormControl, OutlinedInput, InputLabel,
  InputAdornment, IconButton, Alert, Link
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, ArrowForward, Key } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const AuthInput = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '10px',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600', borderWidth: '2px' },
}));

const SubmitButton = styled('button')(({ disabled }) => ({
  width: '100%',
  height: '52px',
  border: 'none',
  borderRadius: '10px',
  background: disabled ? 'rgba(247,166,0,0.4)' : 'linear-gradient(135deg, #F7A600 0%, #FFB52E 100%)',
  color: '#0a0d1a',
  fontWeight: 700,
  fontSize: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s',
  boxShadow: disabled ? 'none' : '0 4px 20px rgba(247,166,0,0.35)',
}));

const tips = [
  'Use at least 8 characters',
  'Mix uppercase and lowercase letters',
  'Include numbers and special characters',
  'Avoid using personal information',
];

const UpdatePassword = () => {
  const navigate = useNavigate();
  const appMode = useSelector((state) => state.mode.mode);

  const [error, setErros] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    code: '', email: '', password: '', password_confirmation: ''
  });

  const onChange = (e) => setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const { isLoading: isSendingRequest, mutate: postLogin } = useMutation(
    async () => await apiClient.post('/api/update-password', {
      code: registerForm.code,
      email: registerForm.email,
      password: registerForm.password,
      password_confirmation: registerForm.password_confirmation,
    }),
    {
      onSuccess: (res) => {
        localStorage.setItem('notification', JSON.stringify({ message: `${res.data.message}`, type: 'success' }));
        window.dispatchEvent(new Event('newMessage'));
        window.dispatchEvent(new Event('processed'));
        navigate('/login');
      },
      onError: (err) => {
        setErros(err.response?.data?.errors || err.message);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = () => {
    window.dispatchEvent(new Event('processing'));
    postLogin();
  };

  const isDark = appMode === 'dark';
  const iconColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: isDark ? '#080c18' : '#f1f5f9' }}>

      {/* ── Left Brand Panel ── */}
      <Box sx={{
        width: { xs: 0, lg: '45%' },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { lg: 5, xl: 7 },
        background: 'linear-gradient(160deg, #080c18 0%, #0d1526 55%, #111e35 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

        <Box>
          <img src="/intracapital-logos/intracapital-logo-white.png" alt="INTRA CAPITAL" style={{ height: '44px', marginBottom: '48px' }} />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ height: '2px', width: '28px', bgcolor: '#F7A600' }} />
            <Typography sx={{ color: '#F7A600', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Security Update
            </Typography>
          </Box>

          <Typography sx={{ fontSize: { lg: '34px', xl: '42px' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            Create a Strong{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #F7A600, #FFD166)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              New Password.
            </Box>
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.7, mb: 6, maxWidth: 360 }}>
            Your account security matters. Choose a strong password to protect your investments and trading activity.
          </Typography>

          {/* Password tips */}
          <Box sx={{ p: 3, borderRadius: '14px', bgcolor: 'rgba(247,166,0,0.05)', border: '1px solid rgba(247,166,0,0.15)', mb: 4 }}>
            <Typography sx={{ color: '#F7A600', fontWeight: 700, fontSize: '13px', mb: 2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Password Tips
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {tips.map((tip, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F7A600', flexShrink: 0 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{tip}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
          © {new Date().getFullYear()} INTRA CAPITAL LIMITED. All rights reserved.
        </Typography>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 5 },
        bgcolor: isDark ? '#0d1117' : '#ffffff',
        overflowY: 'auto',
      }}>
        <Box sx={{ width: '100%', maxWidth: '420px' }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mb: 5 }}>
            <img src={isDark ? '/intracapital-logos/intracapital-logo-white.png' : '/intracapital-logos/intracapital-logo.png'} alt="INTRA CAPITAL" style={{ height: '44px' }} />
          </Box>

          {/* Icon */}
          <Box sx={{
            width: 64, height: 64, borderRadius: '16px', mb: 4,
            background: 'linear-gradient(135deg, rgba(247,166,0,0.15), rgba(247,166,0,0.05))',
            border: '1px solid rgba(247,166,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Key sx={{ fontSize: 30, color: '#F7A600' }} />
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 1 }}>
              Update your password
            </Typography>
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', fontSize: '15px', lineHeight: 1.6 }}>
              Enter the verification code sent to your email along with your new password.
            </Typography>
          </Box>

          {/* Error */}
          {error && typeof error === 'string' && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: '14px' }}>{error}</Alert>
          )}

          {/* Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Verification Code</InputLabel>
              <AuthInput
                error={!!(error?.code)}
                onChange={onChange}
                id="code"
                name="code"
                type="text"
                label="Verification Code"
                startAdornment={
                  <InputAdornment position="start">
                    <Key sx={{ color: iconColor, fontSize: '19px' }} />
                  </InputAdornment>
                }
              />
              {error?.code && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.code}</Typography>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Email Address</InputLabel>
              <AuthInput
                error={!!(error?.email)}
                onChange={onChange}
                id="email"
                type="email"
                name="email"
                label="Email Address"
                startAdornment={
                  <InputAdornment position="start">
                    <Email sx={{ color: iconColor, fontSize: '19px' }} />
                  </InputAdornment>
                }
              />
              {error?.email && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.email}</Typography>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>New Password</InputLabel>
              <AuthInput
                onChange={onChange}
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                startAdornment={
                  <InputAdornment position="start">
                    <Lock sx={{ color: iconColor, fontSize: '19px' }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: iconColor }}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#F7A600' } }}>Confirm New Password</InputLabel>
              <AuthInput
                error={!!(error?.password)}
                onChange={onChange}
                id="password_confirmation"
                name="password_confirmation"
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm New Password"
                startAdornment={
                  <InputAdornment position="start">
                    <Lock sx={{ color: iconColor, fontSize: '19px' }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: iconColor }}>
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {error?.password && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.password}</Typography>}
            </FormControl>

          </Box>

          {/* Submit */}
          <SubmitButton onClick={onSubmit} disabled={isSendingRequest}>
            {isSendingRequest ? 'Updating Password…' : 'Update Password'}
            {!isSendingRequest && <ArrowForward sx={{ fontSize: 18 }} />}
          </SubmitButton>

          {/* Back link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Link component={RouterLink} to="/login" underline="none" sx={{ color: '#F7A600', fontSize: '14px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Sign In
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UpdatePassword;
